<script>
    import { onMount } from 'svelte';
    import AppTabs from '../components/AppTabs.svelte';
    import { isNative } from '../native.js';
    const nativeApp = isNative();
	import { writable, get } from 'svelte/store'; 
    import FaceAnalysis from './face-analysis.svelte';
    import FaceAnalysisResult from './face-analysis-result.svelte';
    import Recommendations from './recommendations.svelte';
    import Footer from '../components/Footer.svelte';
    import Routine from './routine.svelte';
    import AboutSection from '../components/AboutSection.svelte';
    import HowItWorks from '../components/HowItWorks.svelte';
    import AnalysisPreview from '../components/AnalysisPreview.svelte';
    import CuratedPreview from '../components/CuratedPreview.svelte';
    import TokenReceived from './TokenReceived.svelte';
    import Profile from './Profile.svelte';
    import ProductScanner from './product-scanner.svelte';
	import RoutineImport from './routine-import.svelte';
	import Progress from './progress.svelte';
	import NextStep from './next-step.svelte';
	import SkinQuestions from './skin-questions.svelte';
	import ReturningHome from './returning-home.svelte';
	import Privacy from './privacy.svelte';
	import Terms from './terms.svelte';
	import NotFound from './not-found.svelte';
	import DeleteAccount from './delete-account.svelte';
	
	import { currentPage, userStore, recommendationsStore } from '../store.js';
	import { navigate, pageFromPath, AUTH_ONLY_PAGES } from '../router.js';
	import { formDataStore } from '../components/store';

	let isMenuOpen = false;
	let isLoading = false;
	let postAuthLoading = false;  // True while processing post-auth recommendation fetch
	let authResolved = false;

	function toggleMenu() {
		isMenuOpen = !isMenuOpen;
	}

	function closeMenu() {
		isMenuOpen = false;
	}

	// Any navigation closes the menu, so it can never be left covering the page.
	function navigateAndClose(page) {
		closeMenu();
		navigate(page);
	}

	function handleMenuKeydown(event) {
		if (event.key === 'Escape') closeMenu();
	}

	function navIsActive(page) {
		const activePage = get(currentPage);

		if (page === 'my-skin') {
			return ['face-analysis-result', 'face-analysis', 'my-skin', 'skin-questions'].includes(activePage);
		}

		if (page === 'routine') {
			return ['recommendations', 'routine', 'routine-import', 'next-step'].includes(activePage);
		}

		return activePage === page;
	}

	function accountLabel(username) {
		const name = String(username || '').trim();
		return name ? name.split(/\s+/)[0] : 'Account';
	}

    // Store for recommendations data
    // The same store the rest of the app writes to, so recommendations fetched
    // from any screen (the results screen, the next-step screen) show here.
    const recommendationsData = recommendationsStore;

    // Store for routine data
    let routineData = writable([]);

    // Navigation functions with data handling
    function goToRecommendations(data) {
        recommendationsData.set(data);
        navigate('recommendations');
    }

    function goToRoutine(data) {
        routineData.set(data);
        navigate('routine');
    }

	function goToMySkin() {
		const hasSkinProfile = localStorage.getItem('skinProfile') || localStorage.getItem('AoI');
		if (hasSkinProfile) {
			navigate('face-analysis-result');
		} else {
			goToFaceAnalysis();
		}
	}

	async function goToMyRoutine() {
		const existingRecommendations = get(recommendationsData);
		if (existingRecommendations && existingRecommendations.length > 0) {
			navigate('recommendations');
			return;
		}

		const savedProfile = localStorage.getItem('skinProfile');
		if (!savedProfile) {
			navigate('routine-import');
			return;
		}

		postAuthLoading = true;
		currentPage.set('loading');

		try {
			const profile = JSON.parse(savedProfile);
			const recoResponse = await fetch('/api/recommendation_engine', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(profile)
			});

			if (!recoResponse.ok) {
				throw new Error('Failed to load recommendations');
			}

			const recommendations = await recoResponse.json();
			recommendationsData.set(recommendations);
			recommendationsStore.set(recommendations);
			navigate('recommendations');
		} catch (error) {
			console.error('Error loading routine from saved profile:', error);
			navigate('face-analysis-result');
		} finally {
			postAuthLoading = false;
		}
	}

	async function goToFaceAnalysis() {
		console.log('goToFaceAnalysis called');
		if (isLoading) {
			console.log('Already loading, ignoring click');
			return;
		}
		
		isLoading = true;
		try {
			console.log('Making request to /api/warm_up_db');
			const response = await fetch("/api/warm_up_db", {
				method: 'GET',
				headers: {
					'Accept': 'application/json'
				},
				credentials: 'include'
			});

			console.log('Face analyze API response:', {
				status: response.status,
				statusText: response.statusText,
				ok: response.ok
			});

			if (response.ok) {
				const data = await response.json();
				console.log("DB warm up response:", data);
				console.log("DB is warmed up, navigating to face-analysis");
				navigate('face-analysis');
			} else {
				const errorText = await response.text();
				console.error("Failed to warm up database:", {
					status: response.status,
					statusText: response.statusText,
					error: errorText
				});
				alert("Unable to start face analysis. Please try again later.");
			}
		} catch (error) {
			console.error("Fetch error in goToFaceAnalysis:", {
				name: error.name,
				message: error.message,
				stack: error.stack
			});
			alert("Unable to start face analysis. Please try again later.");
		} finally {
			isLoading = false;
		}
	}

	function goToFaceAnalysisResult() {
		navigate('face-analysis-result');
	}

	function goBackToRecommendations() {
		navigate('recommendations');
	}

	function handleRouteChange(event) {
		navigate(event.detail.page);
    }

	// Resolve the session, retrying once through the refresh endpoint.
	// Returns the auth payload, or null when the visitor is signed out.
	async function resolveAuth() {
		try {
			const response = await fetch('/api/is_authenticated', { credentials: 'include' });
			// The server renews an expired access token itself, so a non-ok
			// answer here means signed out, not "try refreshing and ask again".
			return response.ok ? await response.json() : null;
		} catch (error) {
			console.error('Auth resolution failed:', error);
			return null;
		}
	}

	// A new user adds their routine during onboarding, before they have an
	// account, so it lives only in this browser. Once they sign in, save it
	// to their account if the account has none, which is also when each
	// product's start date begins to count.
	async function syncLocalRoutine() {
		let local = [];
		try { local = JSON.parse(localStorage.getItem('currentRoutine') || '[]') || []; } catch (e) { local = []; }
		if (!local.length) return;
		try {
			const existing = await fetch('/api/current-routine', { credentials: 'include' });
			if (!existing.ok) return;
			const data = await existing.json();
			if ((data.items || []).length) return;
			await fetch('/api/current-routine', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				credentials: 'include',
				body: JSON.stringify({ items: local })
			});
		} catch (error) {
			console.error('Routine sync after sign-in failed:', error);
		}
	}

	// After signup/login we may owe the user the recommendations they asked for
	// before they had an account. Returns true if it took over navigation.
	async function handlePendingRecommendations(authData) {
		const hasPendingRequest = localStorage.getItem('pendingRecommendationRequest');
		const savedFormData = localStorage.getItem('formDataForRecommendations');
		if (hasPendingRequest !== 'true' || !savedFormData) return false;

		postAuthLoading = true;
		currentPage.set('loading');

		localStorage.removeItem('pendingRecommendationRequest');
		localStorage.removeItem('formDataForRecommendations');

		try {
			const formData = JSON.parse(savedFormData);
			formData.userId = authData.userId;
			formDataStore.set(formData);

			const recoResponse = await fetch('/api/recommendation_engine', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(formData)
			});

			if (!recoResponse.ok) throw new Error('Failed to load recommendations');

			const recommendations = await recoResponse.json();

			await fetch('/api/store_recommendations', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ userId: authData.userId, recommendations, formData })
			});

			recommendationsData.set(recommendations);
			recommendationsStore.set(recommendations);
			postAuthLoading = false;
			navigate('recommendations');
			return true;
		} catch (error) {
			console.error('[POST_AUTH] Error fetching recommendations:', error);
			postAuthLoading = false;
			return false;
		}
	}

	onMount(async () => {
		// 1. Resolve the route from the URL first, unconditionally. Which page
		//    to render has nothing to do with whether the visitor has a session,
		//    and making it wait on the auth call is how a refresh used to dump
		//    people back on the homepage with their analysis gone.
		const requestedPage = pageFromPath();
		currentPage.set(requestedPage);

		// 2. Resolve auth separately, and let the page react to it.
		const authData = await resolveAuth();

		if (authData) {
			userStore.set({
				isAuthenticated: true,
				username: authData.username,
				userId: authData.userId
			});
			if (authData.accessToken) {
				localStorage.setItem('userToken', authData.accessToken);
			}

			await syncLocalRoutine();
			const tookOver = await handlePendingRecommendations(authData);
			if (tookOver) {
				authResolved = true;
				return;
			}
		} else {
			// Signed out — but keep any anonymous userId, so the visitor's
			// analysis isn't orphaned before they get a chance to save it.
			userStore.signOut();

			// 3. Only redirect for pages that genuinely cannot render without
			//    a session. Everything else stays put.
			if (AUTH_ONLY_PAGES.includes(requestedPage)) {
				navigate('home', { replace: true });
			}
		}

		authResolved = true;
	});

    // Listen to popstate event to handle browser back and forward buttons
    window.onpopstate = () => {
        currentPage.set(pageFromPath());
    };

    // Example function to simulate login (adjust as needed for your app)
    function login() {
		const oldUserId = localStorage.getItem('UserId');
		const pageValue = get(currentPage) || 'home';

		if (oldUserId) {
			window.location.href = `/api/login?currentPage=${pageValue}&oldUserId=${encodeURIComponent(oldUserId)}`;
		} else {
			window.location.href = `/api/login?currentPage=${pageValue}`;
		}
    }

    // Example function to simulate logout
    function logout() {
        userStore.logout();
        localStorage.removeItem('userToken');
        window.location.href = '/api/logout';
    }

	function scrollToSection(event) {
        const targetId = event.currentTarget.getAttribute('href').substring(1);
        const targetElement = document.getElementById(targetId);
        if (targetElement) {
            targetElement.scrollIntoView({ behavior: 'smooth' });
        }
    }

	// The client-side token refresh that used to live here has been removed. It
	// was broken in four independent ways and, more importantly, redundant:
	//
	//   1. it POSTed to /refresh, but the auth blueprint is mounted at /api, so
	//      the real route is /api/refresh. /refresh fell through to the SPA
	//      catch-all, which only accepts GET -- hence the 405 in the console on
	//      every page load for a signed-out visitor.
	//   2. it sent { refreshToken }; the handler reads request.json['refresh_token'].
	//   3. it read the token from localStorage('refreshToken'), which nothing
	//      ever writes. The real refresh token is an httpOnly cookie, which
	//      JavaScript cannot read by design.
	//   4. it stored data.accessToken; the endpoint returns the OIDC payload,
	//      whose field is access_token.
	//
	// None of that needs fixing, because /api/is_authenticated ALREADY refreshes
	// transparently: when the access token has expired it reads the refresh_token
	// cookie, renews server-side, and only returns 401 if that fails. A 401 from
	// it is therefore final, and the correct client behaviour is to treat the
	// visitor as signed out.

	$: contentStyle = isMenuOpen ? 'margin-top: 100px;' : ''; // Adjust margin as needed
</script>

<style>
	/* Dark Editorial Theme */
	* {
		box-sizing: border-box;
		-webkit-font-smoothing: antialiased;
		-moz-osx-font-smoothing: grayscale;
	}

	/* The bar is a fixed --nav-h tall and nothing inside it may change that.
	   Every page's --page-top is calc(--nav-h + --space-12), so a nav that
	   grows pushes its own content under itself. It was 97px on the home page
	   (28px padding + a 41px button) against a 72px token. */
	.navbar {
		display: flex;
		justify-content: space-between;
		align-items: center;
		gap: var(--space-6);
		height: var(--nav-h);
		padding: 0 var(--page-pad-x);
		position: fixed;
		top: 0;
		left: 0;
		width: 100%;
		z-index: var(--z-nav);
		background: var(--scrim);
		backdrop-filter: blur(24px);
		border-bottom: 1px solid var(--border);
	}

	.logo-text {
		font-family: var(--font-display);
		font-size: var(--text-lg);
		font-weight: var(--weight-regular);
		color: var(--cream);
		letter-spacing: var(--track-label);
		text-transform: uppercase;
		text-decoration: none;
		white-space: nowrap;
		flex: 0 0 auto;
	}

	.logo-text:hover { color: var(--cream); }

	/* Eight bordered pills did not fit: on the home page, where About and
	   How It Works also appear, the row overran the bar and the labels wrapped
	   to two lines inside their own borders. Navigation is not a row of
	   buttons. These are links now, marked by a rule under the active one. */
	.nav-links {
		display: flex;
		gap: var(--space-6);
		align-items: center;
		min-width: 0;
	}

	.nav-links a,
	.nav-links button {
		background: transparent;
		border: 0;
		border-radius: var(--radius-0);
		padding: var(--space-2) 0;
		margin: 0;
		color: var(--text-muted);
		font-family: var(--font-body);
		font-size: var(--text-2xs);
		font-weight: var(--weight-medium);
		letter-spacing: var(--track-label);
		text-transform: uppercase;
		text-decoration: none;
		white-space: nowrap;
		cursor: pointer;
		position: relative;
		transition: color var(--transition);
	}

	.nav-links a:hover,
	.nav-links button:hover {
		color: var(--cream);
	}

	/* The marker is a pseudo-element so switching pages cannot change the
	   width of anything, which is what a border or a weight change would do. */
	.nav-links button::after {
		content: '';
		position: absolute;
		left: 0;
		right: 0;
		bottom: -2px;
		height: 1px;
		background: var(--sage);
		transform: scaleX(0);
		transform-origin: left;
		transition: transform var(--transition);
	}

	.nav-links button.active {
		color: var(--cream);
	}

	.nav-links button.active::after {
		transform: scaleX(1);
	}

	.account-area {
		display: flex;
		align-items: center;
		gap: var(--space-5);
		padding-left: var(--space-5);
		margin-left: var(--space-1);
		border-left: 1px solid var(--border);
	}

	/* The one control in the bar that is an action rather than a destination,
	   so it is the one that keeps a border. */
	.nav-links .signin-button {
		border: 1px solid var(--border-strong);
		padding: var(--space-2) var(--space-5);
		color: var(--cream);
	}

	.nav-links .signin-button:hover {
		border-color: var(--sage);
		color: var(--sage);
	}

	.nav-links .signin-button::after { content: none; }

	/* The gradient used to sit on the same element as max-width: 1200px, so
	   above 1200px it stopped at the content edge and drew two visible seams
	   down the page. The wash belongs to the viewport, the grid to the
	   content. */
	.hero-band {
		background:
			linear-gradient(115deg, var(--sage-veil) 0%, transparent 34%),
			linear-gradient(245deg, var(--gold-veil) 0%, transparent 28%);
	}

	.hero {
		min-height: calc(100vh - var(--nav-h));
		display: grid;
		grid-template-columns: minmax(0, 1fr) minmax(0, 0.85fr);
		align-items: center;
		gap: var(--section-gap);
		padding: var(--page-top) var(--page-pad-x) var(--space-20);
		max-width: var(--content-max);
		margin: 0 auto;
	}

	.hero-image {
		justify-self: end;
		width: 100%;
		max-width: 460px;
	}

	.hero-label {
		font-size: var(--text-2xs);
		letter-spacing: var(--track-eyebrow);
		text-transform: uppercase;
		color: var(--sage);
		margin-bottom: var(--space-8);
		font-weight: var(--weight-regular);
		font-family: var(--font-body);
	}

	.hero-title {
		font-family: var(--font-display);
		font-size: var(--text-5xl);
		font-weight: var(--weight-regular);
		line-height: var(--leading-tight);
		letter-spacing: var(--track-tight);
		color: var(--cream);
		margin-bottom: var(--space-7);
	}

	.hero-title em {
		font-style: italic;
		color: var(--rose);
	}

	.hero-desc {
		font-size: var(--text-base);
		color: var(--text-muted);
		max-width: 46ch;
		line-height: var(--leading-relaxed);
		margin-bottom: var(--space-10);
		font-weight: var(--weight-light);
		font-family: var(--font-body);
		text-align: left;
	}

	#loadFaceAnalysis, #goToProfile {
		background: transparent;
		color: var(--cream);
		border: 1px solid var(--border-strong);
		padding: var(--space-4) var(--space-12);
		font-size: var(--text-2xs);
		letter-spacing: var(--track-label);
		text-transform: uppercase;
		cursor: pointer;
		transition: background var(--transition), border-color var(--transition), color var(--transition);
		font-family: var(--font-body);
		display: inline-block;
		border-radius: var(--radius-0);
		box-shadow: none;
		margin: 0;
	}

	#loadFaceAnalysis:hover, #goToProfile:hover {
		background: var(--sage);
		border-color: var(--sage);
		color: var(--bg);
		transform: none;
		box-shadow: none;
	}

	.hero-visual {
		width: 100%;
		max-width: 460px;
		aspect-ratio: 3 / 4;
		height: auto;
		border-radius: 8px;
		position: relative;
		overflow: hidden;
		box-shadow: var(--panel-shadow);
	}

	.hero-visual img {
		width: 100%;
		height: 100%;
		object-fit: cover;
		border-radius: 8px;
		filter: brightness(0.95) contrast(1.05);
	}

	.scan-overlay {
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		z-index: 2;
		pointer-events: none;
	}

	.scan-line {
		position: absolute;
		left: 10%;
		right: 10%;
		height: 2px;
		background: linear-gradient(90deg, transparent 0%, var(--sage) 20%, var(--gold) 50%, var(--sage) 80%, transparent 100%);
		box-shadow: 0 0 20px var(--sage-glow), 0 0 60px var(--gold-line);
		animation: scanFace 4s ease-in-out infinite;
		opacity: 0.8;
	}

	@keyframes scanFace {
		0% { top: 15%; opacity: 0; }
		10% { opacity: 0.8; }
		90% { opacity: 0.8; }
		100% { top: 75%; opacity: 0; }
	}

	.scan-label {
		position: absolute;
		bottom: 24px;
		left: 24px;
		z-index: 3;
		font-size: var(--text-3xs);
		letter-spacing: 3px;
		text-transform: uppercase;
		color: var(--sage);
		background: var(--scrim);
		backdrop-filter: blur(8px);
		padding: var(--space-2) var(--space-4);
		border: 1px solid var(--sage-line);
		border-radius: 4px;
		font-family: var(--font-body);
	}

	.scan-dots {
		position: absolute;
		top: 20px;
		right: 20px;
		z-index: 3;
		display: flex;
		gap: var(--space-1);
		align-items: center;
	}

	.scan-dot {
		width: 6px;
		height: 6px;
		border-radius: var(--radius-circle);
		background: var(--sage);
		animation: dotPulse 2s ease-in-out infinite;
	}

	.scan-dot:nth-child(2) { animation-delay: 0.3s; }
	.scan-dot:nth-child(3) { animation-delay: 0.6s; }

	@keyframes dotPulse {
		0%, 100% { opacity: 0.3; }
		50% { opacity: 1; }
	}

	.scan-status {
		position: absolute;
		top: 20px;
		right: 48px;
		z-index: 3;
		font-size: var(--text-3xs);
		letter-spacing: 2px;
		text-transform: uppercase;
		color: var(--gold);
		opacity: 0.7;
		font-family: var(--font-body);
	}

	#content {
		padding-top: 0;
	}

	.hero-proof {
		display: grid;
		grid-template-columns: repeat(3, minmax(0, 1fr));
		gap: var(--space-px);
		background: var(--border);
		margin-top: var(--space-7);
		max-width: 520px;
	}

	.hero-proof span {
		background: var(--scrim);
		color: var(--text-muted);
		font-size: var(--text-2xs);
		letter-spacing: var(--track-label);
		text-transform: uppercase;
		padding: var(--space-3);
		text-align: center;
	}

	.hero-disclaimer {
		margin-top: var(--space-5);
		max-width: 52ch;
		font-size: var(--text-xs);
		line-height: var(--leading-normal);
		color: var(--text-muted);
		font-family: var(--font-body);
	}

	.hamburger {
		display: none;
		background: transparent;
		border: none;
		cursor: pointer;
		z-index: var(--z-menu);
		padding: var(--space-2);
		width: 40px;
		height: 40px;
	}

	.hamburger-bar {
		display: block;
		width: 24px;
		height: 1px;
		background: var(--cream);
		margin: var(--space-1) 0;
		transition: transform var(--dur), opacity var(--dur);
	}

	/* Becomes an X when open, so there's an obvious way out. */
	.hamburger.is-open .hamburger-bar:nth-child(1) {
		transform: translateY(7px) rotate(45deg);
	}

	.hamburger.is-open .hamburger-bar:nth-child(2) {
		opacity: 0;
	}

	.hamburger.is-open .hamburger-bar:nth-child(3) {
		transform: translateY(-7px) rotate(-45deg);
	}

	.menu-backdrop {
		display: none;
	}

	@media (max-width: 768px) {
		.menu-backdrop {
			display: block;
			position: fixed;
			inset: 0;
			z-index: var(--z-menu);
			background: var(--scrim);
			border: 0;
			padding: 0;
		}
	}

	@media (max-width: 768px) {
		.hero {
			grid-template-columns: 1fr;
			min-height: 0;
			gap: var(--space-12);
			padding: var(--page-top) var(--page-pad-x) var(--space-16);
			text-align: center;
		}

		/* --text-5xl clamps from 68px to 44px on its own, so the old 48px
		   override is gone. */
		.hero-desc {
			text-align: center;
			margin-left: auto;
			margin-right: auto;
		}

		.hero-proof {
			grid-template-columns: 1fr;
			width: 100%;
		}

		.hero-image {
			justify-self: center;
			max-width: 320px;
		}

		.navbar {
			padding: 0 var(--page-pad-x);
		}

		.hamburger {
			display: block;
		}

		/* The menu is hidden by default and shown when `.open` is applied.
		   This used to be inverted -- the class applied when the menu OPENED
		   was called `hidden` -- which is how it ended up shipping
		   see-through and impossible to dismiss. */
		.nav-links {
			display: none;
			flex-direction: column;
			position: absolute;
			top: 100%;
			width: 100%;
			right: 0;
			left: 0;
			/* Fully opaque: at 0.95 with a backdrop-filter that wasn't taking
			   effect, the hero headline read straight through the menu. */
			background: var(--overlay);
			padding: var(--space-6);
			gap: var(--space-5);
			border-bottom: 1px solid var(--border);
			box-shadow: 0 24px 48px var(--scrim);
			max-height: calc(100vh - 80px);
			overflow-y: auto;
		}

		.nav-links a {
			display: block;
			text-align: center;
			padding: var(--space-2) var(--space-5);
		}

		.nav-links.open {
			display: flex;
		}

		.account-area {
			flex-direction: column;
			width: 100%;
			padding-left: 0;
			border-left: 0;
			gap: var(--space-4);
		}

		.nav-links .account-button,
		.nav-links .logout-button {
			width: 100%;
		}
	}

	/* Post-auth loading screen */
	.loading-screen {
		min-height: 100vh;
		display: flex;
		align-items: center;
		justify-content: center;
		background: var(--bg);
	}

	.loading-content {
		text-align: center;
	}

	.loading-content h2 {
		font-family: var(--font-display);
		font-size: var(--text-xl);
		font-weight: var(--weight-regular);
		color: var(--cream);
		margin-bottom: var(--space-10);
	}

	.loading-bar {
		width: 300px;
		height: 2px;
		background: var(--border);
		margin: 0 auto;
		overflow: hidden;
	}

	.loading-bar-fill {
		height: 100%;
		width: 40%;
		background: var(--rose);
		animation: loading-slide 1.5s ease-in-out infinite;
	}

	@keyframes loading-slide {
		0% { transform: translateX(-100%); }
		100% { transform: translateX(350%); }
	}
	.native-app { padding-bottom: calc(76px + env(safe-area-inset-bottom, 0px)); }
</style>

<div id="container" class:native-app={nativeApp}>
	{#if $currentPage !== 'loading' && !postAuthLoading}
		<nav class="navbar">
			<a href="/" on:click|preventDefault={() => navigateAndClose('home')} class="logo-text" style="cursor:pointer;">SkinGenie</a>
			<div class="nav-links" class:open={isMenuOpen} id="primary-menu">
				{#if $currentPage === 'home' && !(authResolved && $userStore.isAuthenticated)}
					<a href="#about" on:click|preventDefault={(e) => { closeMenu(); scrollToSection(e); }}>About</a>
					<a href="#how-it-works" on:click|preventDefault={(e) => { closeMenu(); scrollToSection(e); }}>How It Works</a>
				{/if}
				<button class:active={navIsActive('my-skin')} on:click={() => { closeMenu(); goToMySkin(); }}>My Skin</button>
				<button class:active={navIsActive('scanner')} on:click={() => navigateAndClose('scanner')}>Check Product</button>
				<button class:active={navIsActive('routine')} on:click={() => { closeMenu(); goToMyRoutine(); }}>My Routine</button>
				<button class:active={navIsActive('progress')} on:click={() => navigateAndClose('progress')}>Progress</button>
				{#if authResolved}
					{#if $userStore.isAuthenticated}
						<div class="account-area">
							<button class="account-button" class:active={navIsActive('profile')} on:click={() => navigateAndClose('profile')}>
								{accountLabel($userStore.username)}
							</button>
							<button class="logout-button" on:click={logout}>Log Out</button>
						</div>
					{:else}
						<button class="signin-button" on:click={login}>Sign In</button>
					{/if}
				{/if}
			</div>
			<button
				class="hamburger"
				class:is-open={isMenuOpen}
				on:click={toggleMenu}
				aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
				aria-expanded={isMenuOpen}
				aria-controls="primary-menu"
			>
				<span class="hamburger-bar"></span>
				<span class="hamburger-bar"></span>
				<span class="hamburger-bar"></span>
			</button>
		</nav>
		{#if isMenuOpen}
			<!-- Tapping anywhere outside the menu closes it. Without this the
			     only way out was finding the hamburger again, and the open menu
			     sat on top of the page swallowing every click underneath. -->
			<div
				class="menu-backdrop"
				on:click={closeMenu}
				on:keydown={handleMenuKeydown}
				role="button"
				tabindex="-1"
				aria-label="Close menu"
			></div>
		{/if}
	{/if}
	{#if $currentPage === 'home' && authResolved && $userStore.isAuthenticated}
		<ReturningHome {goToFaceAnalysis} />
		<Footer />
	{:else if $currentPage === 'home'}
		<div id="content" style={contentStyle}>
			<section class="hero-band"><div class="hero">
				<div class="hero-content">
					<div class="hero-label">AI-Powered Skincare</div>
					<h1 class="hero-title">The future of skincare is <em>personal</em></h1>
					<p class="hero-desc">Computer vision reads your skin, then we build a routine around what it actually needs, with the reasoning shown for every product.</p>
					{#if $userStore.isAuthenticated}
						<button id="goToProfile" on:click={() => navigate('profile')}>My Profile</button>
					{:else}
						<button
							id="loadFaceAnalysis"
							on:click={goToFaceAnalysis}
							disabled={isLoading}
						>
							{#if isLoading}
								Loading...
							{:else}
								Analyze Your Skin
							{/if}
						</button>
					{/if}
					<div class="hero-proof" aria-label="SkinGenie product promise">
						<span>Skin profile</span>
						<span>Product scanner</span>
						<span>Routine fit</span>
					</div>
					<p class="hero-disclaimer">
						Skincare suggestions, not medical advice. SkinGenie can&rsquo;t diagnose a skin condition.
					</p>
				</div>
				<div class="hero-image">
					<div class="hero-visual">
						<img src="/static/hero-analysis.png" alt="AI skin analysis visualization">
						<div class="scan-overlay">
							<div class="scan-line"></div>
						</div>
						<div class="scan-dots">
							<div class="scan-dot"></div>
							<div class="scan-dot"></div>
							<div class="scan-dot"></div>
						</div>
						<span class="scan-status">Analyzing</span>
						<div class="scan-label">AI Skin Analysis Active</div>
					</div>
				</div>
			</div></section>

			<HowItWorks />
			<AnalysisPreview />
			<CuratedPreview />
			<AboutSection />
			<Footer />
		</div>
	{:else if $currentPage === 'loading' || postAuthLoading}
		<div class="loading-screen">
			<div class="loading-content">
				<h2>Curating your personalized recommendations...</h2>
				<div class="loading-bar"><div class="loading-bar-fill"></div></div>
			</div>
		</div>
	{:else if $currentPage === 'profile'}
		<Profile {goToRecommendations} />
		<Footer />
	{:else if $currentPage === 'scanner'}
		<ProductScanner />
		<Footer />
	{:else if $currentPage === 'routine-import'}
		<RoutineImport />
		<Footer />
	{:else if $currentPage === 'next-step'}
		<NextStep />
		<Footer />
	{:else if $currentPage === 'progress'}
		<Progress />
		<Footer />
	{:else if $currentPage === 'face-analysis'}
		<FaceAnalysis {currentPage} {goToFaceAnalysisResult} />
	{:else if $currentPage === 'face-analysis-result'}
		<FaceAnalysisResult {goToRecommendations} />
	{:else if $currentPage === 'skin-questions'}
		<SkinQuestions />
		<Footer />
	{:else if $currentPage === 'recommendations'}
		<Recommendations recommendations={recommendationsData} goToRoutine={goToRoutine} />
		<Footer />
	{:else if $currentPage === 'routine'}
		<Routine routine={routineData} goBackToRecommendations={goBackToRecommendations} />
		<Footer />
	{:else if $currentPage === 'privacy'}
		<Privacy />
		<Footer />

	{:else if $currentPage === 'terms'}
		<Terms />
		<Footer />

	{:else if $currentPage === 'delete-account'}
		<DeleteAccount />
		<Footer />

	{:else if $currentPage === 'not-found'}
		<NotFound />
		<Footer />

	{:else if $currentPage === 'token-received'}
		<TokenReceived on:routeChange={handleRouteChange} />
	{/if}
</div>

{#if nativeApp && $currentPage !== 'loading' && !postAuthLoading && $currentPage !== 'face-analysis'}
    <AppTabs page={$currentPage} onSkin={goToMySkin} onRoutine={goToMyRoutine} />
{/if}
